import React, { useEffect, useState } from "react";
import { Clock, Trash2, CalendarDays, AlertTriangle } from "lucide-react";
import dayjs from "dayjs";

export default function BetSummaryPopup({
  isOpen,
  onClose,
  drawTime,
  drawDate,
  onRemoveBet,
  onSubmit,
  betData,
  betTotal,
  onSelectedDrawTime,
  clearAllBets,
  showAllBets = true // Set to true by default
}) {
  const [visible, setVisible] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [drawTimes, setDrawTimes] = useState("");
  const [countDrawTimes, setcountDrawTimes] = useState(0);
  const [betsByGame, setBetsByGame] = useState({});
  
  // Calculate total from actual bet data
  const calculatedTotal = betData?.length > 0 
    ? betData.reduce((sum, bet) => sum + (+bet.amount || 0), 0)
    : 0;

  useEffect(() => {
    if (betData?.length > 0) {
      // Format draw times display
      const formattedTimes = betData.map((bet) => {
        const [hours, minutes] = bet.drawTime?.split(':') || ['00', '00'];
        const period = hours >= 12 ? 'PM' : 'AM';
        const displayHour = hours % 12 || 12;
        return `${displayHour}${period}`;
      });
      
      const uniqueTimes = [...new Set(formattedTimes)];
      setcountDrawTimes(uniqueTimes.length);
      setDrawTimes(uniqueTimes.length === 1 
        ? uniqueTimes[0] 
        : uniqueTimes.join('/'));
      
      // Group bets by game
      const gameGroups = {};
      betData.forEach(bet => {
        const gameId = bet.gameId;
        if (!gameGroups[gameId]) {
          gameGroups[gameId] = [];
        }
        gameGroups[gameId].push(bet);
      });
      setBetsByGame(gameGroups);
    } else {
      setDrawTimes("");
      setcountDrawTimes(0);
      setBetsByGame({});
    }
  }, [betData]);

  useEffect(() => {
    if (isOpen) {
      setVisible(true); // Add popup to DOM
      setTimeout(() => setMounted(true), 10); // Add slight delay for animation to start
    } else {
      setMounted(false); // Trigger exit animation
      setTimeout(() => setVisible(false), 300); // Remove from DOM after animation
    }
  }, [isOpen]);

  // Force re-render when betData changes to ensure popup shows updated bets
  useEffect(() => {
    if (isOpen && betData?.length > 0) {
      // Ensure betsByGame is updated correctly
      const gameGroups = {};
      betData.forEach(bet => {
        const gameId = bet.gameId;
        if (!gameGroups[gameId]) {
          gameGroups[gameId] = [];
        }
        gameGroups[gameId].push(bet);
      });
      setBetsByGame(gameGroups);
    }
  }, [isOpen, betData]);

  if (!visible) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    onClose();
  };

  const renderBets = () => {
    if (betData.length > 0) {
      // Multi-game view
      return Object.entries(betsByGame).map(([gameId, gameBets], gameIndex) => {
        // Get the game name from the first bet in the group
        const gameName = gameBets[0].gameName || "Game";
        const gameType = gameBets[0].gameType || "";
        
        return (
          <div key={gameId} className="mb-4">
            <div className="bg-blue-100 p-2 rounded-t-lg">
              <h3 className="font-semibold">{gameName.toUpperCase()} - {gameType}</h3>
            </div>
            <div className="grid grid-cols-[1fr,80px,100px,40px] gap-2 p-2.5 text-sm font-medium text-gray-600 bg-[#E1E7EF]">
              <div>COMBINATION</div>
              <div>TYPE</div>
              <div>AMOUNT</div>
              <div></div>
            </div>
            <div className="mb-2">
              {gameBets.map((bet, index) => (
                <div
                  key={`${gameId}-${index}`}
                  className="grid grid-cols-[1fr,80px,100px,40px] gap-2 p-2.5 items-center border-b"
                >
                  <div className="font-normal">{bet.combination}</div>
                  <div>{bet.bettingOption === 'target' ? 'S' : 'R'}</div>
                  <div>{bet.amount.toFixed(2)}</div>
                  <button
                    onClick={() => onRemoveBet(betData.findIndex(b => 
                      b.combination === bet.combination && 
                      b.drawTime === bet.drawTime && 
                      b.gameId === bet.gameId &&
                      b.amount === bet.amount))}
                    className="text-red-500 hover:text-red-600"
                    aria-label="Remove bet"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        );
      });
    } else {
      return (
        <div className="p-4 text-center text-gray-500">
          No bets added yet
        </div>
      );
    }
  };

  // Check if bets are from multiple games before showing submit button
  const hasMultipleGames = Object.keys(betsByGame).length > 1;
  const showSubmitWarning = hasMultipleGames;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end justify-center z-[60] font-poppins">
      <div
        className={`w-full max-w-full bg-white rounded-t-2xl p-4 transform transition-transform duration-300 max-h-[90vh] overflow-y-auto ${
          mounted ? "translate-y-0" : "translate-y-full"
        }`}
      >
        {/* Header */}
        <div className="mb-6 relative flex items-center justify-center">
          <h2 className="font-semibold">Bet Summary</h2>
          {betData.length > 0 && (
            <button
              onClick={clearAllBets}
              className="absolute left-0 top-0 text-xs text-red-500 hover:text-red-600 flex items-center"
              aria-label="Clear all bets"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 6h18"></path>
                <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
                <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
              </svg>
              <span className="ml-1">Clear All</span>
            </button>
          )}
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 absolute right-0 top-0"
            aria-label="Close"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
          {betData.length === 0 && (
            <div className="text-xs text-center text-blue-600 mt-1 absolute top-6 left-0 right-0">
              No bets added yet
            </div>
          )}
        </div>

        {/* Draw Info */}
        <div className="grid grid-cols-2 gap-4">
          <div className="flex gap-2 border border-gray-200 rounded-lg p-3">
            <div>
              <Clock className="w-6 h-6 text-gray-500" />
            </div>
            <div className="text-gray-500 text-xs">
              Draw Time{countDrawTimes > 1 ? "(s)" : ""}
              <p className="text-base text-black font-semibold">{drawTimes}</p>
            </div>
          </div>

          <div className="flex gap-2 border border-gray-200 rounded-lg p-3">
            <div>
              <CalendarDays className="w-6 h-6 text-gray-500" />
            </div>
            <div className="text-gray-500 text-xs">
              Draw Date
              <p className="text-base text-black font-semibold">{drawDate}</p>
            </div>
          </div>
        </div>

        {/* Bets Table */}
        <div className="my-5">
          <div className="bg-white">
            {renderBets()}
          </div>
        </div>

        {/* Multi-game info */}
        {showSubmitWarning && (
          <div className="bg-blue-100 p-4 mb-4 rounded-lg flex flex-col items-start gap-3">
            <div className="flex items-start gap-3 w-full">
              <AlertTriangle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm flex-1">
                <p className="font-medium text-blue-800">Multiple Games</p>
                <p className="text-blue-700 mb-2">
                  You have bets for {Object.keys(betsByGame).length} different games in your cart. All games will be submitted in a single transaction.
                </p>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  {Object.entries(betsByGame).map(([gameId, gameBets], index) => (
                    <div key={gameId} className="bg-white p-2 rounded border border-blue-200">
                      <div className="font-medium">{gameBets[0].gameName}</div>
                      <div className="flex justify-between">
                        <span>Bets: {gameBets.length}</span>
                        <span>₱{gameBets.reduce((sum, bet) => sum + (+bet.amount || 0), 0).toFixed(2)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Total */}
        <div className="py-4 flex items-center justify-between">
          <div className="text-base font-normal">Total</div>
          <div className="text-3xl font-normal">₱{calculatedTotal.toFixed(2)}</div>
        </div>

        {/* Action Buttons */}
        <div className="py-4 pt-0 mb-72 max-xs:mb-20">
          {betData.length > 0 && (
            <button
              onClick={clearAllBets}
              className="w-full mb-3 bg-white border border-red-500 text-red-500 hover:bg-red-50 py-3 px-4 rounded-md transition-colors"
            >
              CLEAR ALL BETS
            </button>
          )}
          <button
            onClick={onSubmit}
            className="w-full bg-[#0E64D1] hover:bg-blue-600 text-white py-3 px-4 rounded-md transition-colors"
          >
            {hasMultipleGames ? `SUBMIT ALL BETS (${betData.length})` : 'SUBMIT'}
          </button>
        </div>
      </div>
    </div>
  );
}
